import React, { useState } from "react";
import { Box, Typography, alpha } from "@mui/material";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Colors,
  ChartOptions,
  TooltipModel,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ChartJsZoomPlugin from "chartjs-plugin-zoom";
type ChartJSOrUndefined<
  TType extends import("chart.js").ChartType,
  TData,
  TLabel,
> = ChartJS<TType, TData, TLabel> | undefined;
import ChartControls from "@pages/PhysicianReview/components/DVHChart/ChartControls";
import StructuresSelectbox from "@pages/PhysicianReview/components/DVHChart/StructuresSelectbox";
import { DvhChartData } from "@providers/hop-ord-server/api";
import { externalTooltipHandler } from "./DVHChart/ChartTooltip";

interface Props {
  data: DvhChartData;
}

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Colors,
  ChartJsZoomPlugin,
);
export const CHART_BACKGROUND_COLOR = "#111111";

const DVHChart: React.FC<Props> = ({ data }: Props) => {
  const chartRef = React.useRef<ChartJSOrUndefined<"line", number[], number>>();

  const doseIndex = data.DVHPointFields.indexOf("Dose");
  const volumeIndex = data.DVHPointFields.indexOf(
    "RatioOfTotalStructureVolume",
  );

  // Format dvhData to chart data
  const datasets = data.Structures.map((structure) => {
    const dataPoints = structure.DVHPoints.map((point) => {
      return { x: point[doseIndex], y: point[volumeIndex] };
    });

    return {
      label: structure.StructureName,
      data: dataPoints,
      backgroundColor: alpha(structure.Colour, 0.6),
      borderColor: alpha(structure.Colour, 0.8),
      borderWidth: 2,
      pointRadius: 0.05,
      pointHitRadius: 8,
      pointHoverRadius: 6,
    };
  });

  const maxDose = Math.max(
    ...data.Structures.map((structure) =>
      Math.max(...structure.DVHPoints.map((point) => point[doseIndex])),
    ),
  );

  // Custom plugin for changing background color of the chart
  const backgroundColorPlugin = {
    id: "customCanvasBackgroundColor",
    beforeDraw: (chart: ChartJS) => {
      const { ctx } = chart;
      ctx.save();
      ctx.globalCompositeOperation = "destination-over";
      ctx.fillStyle = CHART_BACKGROUND_COLOR;
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    },
  };

  // Chart Options
  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    layout: {
      padding: {
        top: 0,
        right: 24,
        bottom: 24,
        left: 10,
      },
    },
    scales: {
      x: {
        min: 0,
        max: maxDose,

        title: {
          text: `Dose (${data.DoseUnit})`,
          display: true,
          color: "rgba(255, 255, 255, 0.8)",
          font: {
            size: 16,
          },
        },
        type: "linear",
        grid: {
          color: "rgba(255, 255, 255, 0.4)",
        },
        ticks: {
          color: "rgba(255, 255, 255, 0.8)",
          font: {
            size: 16,
          },
        },
      },
      y: {
        min: 0,
        max: 100,
        title: {
          text: "Volume (%)",
          display: true,
          color: "rgba(255, 255, 255, 0.8)",
          font: {
            size: 16,
          },
        },
        type: "linear",
        grid: {
          color: "rgba(255, 255, 255, 0.4)",
        },
        ticks: {
          color: "rgba(255, 255, 255, 0.8)",
          font: {
            size: 16,
          },
        },
      },
    },

    onHover: () => {
      const panEnabled = chartRef.current?.options.plugins?.zoom?.pan?.enabled;
      const zoomDragEnabled =
        chartRef.current?.options.plugins?.zoom?.zoom?.drag?.enabled;

      if (panEnabled) {
        (chartRef.current?.canvas as HTMLCanvasElement).style.cursor = "move";
      } else if (zoomDragEnabled) {
        (chartRef.current?.canvas as HTMLCanvasElement).style.cursor =
          "crosshair";
      }
    },

    plugins: {
      tooltip: {
        enabled: false,
        position: "nearest",
        external: (context: {
          chart: ChartJS;
          tooltip: TooltipModel<"line">;
        }) =>
          externalTooltipHandler({
            chart: context.chart,
            tooltip: context.tooltip,
          }),
      },

      legend: {
        position: "bottom",
        labels: {
          color: "rgba(255, 255, 255, 0.8)",
        },
      },
      zoom: {
        zoom: {
          mode: "xy",
          scaleMode: "xy",
          drag: {
            enabled: true,
            maintainAspectRatio: true,
          },
        },
        pan: {
          enabled: false,
          mode: "xy",
        },
      },
    },
  };

  // Selected Chart Data
  const [selectedChartData, setSelectedChartData] = useState<ChartData<"line">>(
    { datasets },
  );

  if (!data) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Loading DVH data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          flexGrow: 1,
        }}
      >
        <StructuresSelectbox
          chartData={{ datasets }}
          setSelectedChartData={setSelectedChartData}
        />

        <ChartControls chartRef={chartRef} />

        <Line
          height={200}
          ref={chartRef}
          options={chartOptions}
          data={selectedChartData}
          plugins={[backgroundColorPlugin]}
        />
      </Box>
    </Box>
  );
};

export default DVHChart;
